import { useStore } from "zustand";
import { useDashboardName } from "../../store/headerName";
import { useEffect, useRef } from "react";
import CustomBreadcrumb from "../../components/CustomBreadcrumb";
import FormItemInput from "../../components/form-item/form-item-input";
import FormItemInputSelect from "../../components/form-item/form-item-input-select";
import { Form } from "antd";
import FormCard from "../../components/card/FormCard";
import FormButton from "../../components/FormButton";
import { $api, type paths } from "../../helper";
import { GetFullMediaPath, requireRules } from "../../helper/method";
import { statusShowMock, tokenMock } from "../../helper/mock";
import { useNavigate, useSearchParams } from "react-router";
import { useNotify } from "../../helper/notify";
import Quill from "quill";

export default function AddDepartment() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const notify = useNotify();
  const editorRef = useRef<HTMLDivElement | null>(null);
  const quillRef = useRef<Quill | null>(null);
  const u_id = searchParams.get("u_id");
  const addCategory = $api.useMutation("post", "/site-api/v1/department/");
  const update = $api.useMutation("put", "/site-api/v1/department/");
  const detail = $api.useMutation("get", "/site-api/v1/department-detail/");
  const basicData = $api.useQuery("get", "/site-api/v1/department-form/", {
    headers: {
      Authorization: tokenMock,
      lang: "fa",
    },
  });

  const { setDashboardName } = useStore(useDashboardName);
  useEffect(() => {
    setDashboardName("ایجاد دپارتمان");
    if (u_id) {
      detail.mutate({
        params: {
          query: {
            department_u_id: u_id ?? "",
          },
        },
        headers: {
          Authorization: tokenMock,
          lang: "fa",
        },
      });
    }
    if (!editorRef.current) return;

    if (quillRef.current) return;

    quillRef.current = new Quill(editorRef.current, {
      theme: "snow",
      modules: {
        toolbar: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ["bold", "italic", "underline", "strike"],
          [{ color: [] }, { background: [] }],
          [{ direction: "rtl" }],
          [{ align: [] }],
          ["link"],
          ["clean"],
        ],
      },
    });
  }, []);

  useEffect(() => {
    if (quillRef.current) {
      quillRef.current.format("align", "right");
      quillRef.current.on("text-change", () => {
        form.setFieldValue(
          "end_page_text",
          Number(quillRef.current?.editor.delta.ops[0].insert?.length) <= 1
            ? ""
            : quillRef.current && quillRef.current.root.innerHTML,
        );
      });
    }
  }, [quillRef.current?.editor.getText]);

  useEffect(() => {
    if (u_id && detail.data?.data) {
      Object.entries(detail.data?.data).forEach(([key, value]) => {
        form.setFieldValue(key, value?.toString());
      });
    }
    quillRef.current?.clipboard.dangerouslyPasteHTML(
      detail?.data?.data?.end_page_text ?? "",
    );
  }, [detail.data]);

  const onFinish = (
    values: paths["/site-api/v1/department/"]["post"]["requestBody"]["content"]["application/json"],
    redirect = true,
  ) => {
    if (u_id) {
      update.mutate(
        {
          body: { ...values, department_u_id: u_id },
          headers: {
            Authorization: tokenMock,
            lang: "fa",
          },
        },
        {
          onSuccess: (data) => {
            notify.success(data.message);
            if (redirect) {
              navigate("/department-category");
            } else {
              setTimeout(() => {
                window.location.href = window.location.pathname;
              }, 1000);
            }
          },
        },
      );
    } else {
      addCategory.mutate(
        {
          body: values,
          headers: {
            Authorization: tokenMock,
            lang: "fa",
          },
        },
        {
          onSuccess: (data) => {
            notify.success(data.message);
            if (redirect) {
              navigate("/department-category");
            } else {
              setTimeout(() => {
                window.location.href = window.location.pathname;
              }, 1000);
            }
          },
        },
      );
    }
  };

  return (
    <Form
      form={form}
      onFinish={onFinish}
      layout="vertical"
      className="flex! flex-col! gap-6!"
    >
      <CustomBreadcrumb
        items={[
          {
            title: "دپارتمان ها",
            href: "/department-category",
          },
          {
            title: "ایجاد دپارتمان",
          },
        ]}
      />
      <FormCard
        subTitle="تعریف و تنظیمات پایه مربوط به دپارتمان"
        title="اطلاعات اصلی دپارتمان"
      >
        <div className="w-full grid sm:grid-cols-2 grid-cols-1 gap-6">
          <FormItemInput
            name={"title"}
            rules={requireRules("نام دپارتمان")}
            className="col-span-1"
            label="نام دپارتمان"
            inputProps={{
              placeholder: "لطفا نام دپارتمان خود را وارد کنید.",
            }}
          />
          <FormItemInput
            name={"slug"}
            rules={requireRules("اسلاگ")}
            className="col-span-1"
            label="اسلاگ"
            inputProps={{
              placeholder: "لطفا نام اسلاگ خود را وارد کنید.",
            }}
          />
          <FormItemInput
            name={"box_title"}
            rules={requireRules("عنوان")}
            className="col-span-1"
            label="عنوان"
            inputProps={{
              placeholder: "لطفا عنوان خود را وارد کنید.",
            }}
          />
          <FormItemInput
            name={"box_sub_title"}
            rules={requireRules("عنوان فرعی")}
            className="col-span-1"
            label="عنوان فرعی"
            inputProps={{
              placeholder: "لطفا عنوان فرعی خود را وارد کنید.",
            }}
          />
          <FormItemInput
            name={"sub_title"}
            rules={requireRules("عنوان فرعی دپارتمان")}
            className="col-span-1"
            label="عنوان فرعی دپارتمان"
            inputProps={{
              placeholder: "لطفا عنوان فرعی دپارتمان خود را وارد کنید.",
            }}
          />
          <FormItemInput
            name={"page_title"}
            rules={requireRules("عنوان صفحه")}
            className="col-span-1"
            label="عنوان صفحه"
            inputProps={{
              placeholder: "لطفا عنوان صفحه خود را وارد کنید.",
            }}
          />
          <FormItemInput
            name={"page_sub_title"}
            rules={requireRules("عنوان فرعی صفحه")}
            className="col-span-1"
            label="عنوان فرعی صفحه"
            inputProps={{
              placeholder: "لطفا عنوان فرعی صفحه خود را وارد کنید.",
            }}
          />
          <FormItemInputSelect
            className="col-span-1"
            rules={requireRules("تصویر بنر")}
            name={"image"}
            label="تصویر بنر"
            inputProps={{
              placeholder: "لطفا تصویر بنر خود را انتخاب کنید.",
              options: basicData?.data?.data.map((e) => ({
                label: (
                  <img className="w-35 h-35" src={GetFullMediaPath(e.image)} />
                ),
                value: e.number,
              })),
            }}
          />
          <FormItemInputSelect
            className="col-span-1"
            rules={requireRules("وضعیت")}
            name={"status"}
            label="وضعیت"
            inputProps={{
              placeholder: "لطفا وضعیت نمایش محصول خود را انتخاب کنید.",
              options: statusShowMock,
            }}
          />
        </div>
      </FormCard>
      <FormCard
        subTitle="تعریف و تنظیمات پایه مربوط بخش 1 دپارتمان"
        title="اطلاعات اصلی بخش 1 دپارتمان"
      >
        <div className="w-full grid sm:grid-cols-2 grid-cols-1 gap-6">
          <FormItemInput
            name={"first_item_title"}
            rules={requireRules("عنوان")}
            className="col-span-1"
            label="عنوان"
            inputProps={{
              placeholder: "لطفا عنوان خود را وارد کنید.",
            }}
          />
          <FormItemInput
            name={"first_item_sub_title"}
            rules={requireRules("عنوان فرعی")}
            className="col-span-1"
            label="عنوان فرعی"
            inputProps={{
              placeholder: "لطفا عنوان فرعی خود را وارد کنید.",
            }}
          />
          <FormItemInput
            name={"first_item_link"}
            rules={[
              ...requireRules("لینک"),
              {
                type: "url",
                message: "لینک نادرست است",
              },
            ]}
            className="col-span-1"
            label="لینک"
            inputProps={{
              placeholder: "لطفا لینک خود را وارد کنید.",
            }}
          />
          <FormItemInputSelect
            className="col-span-1"
            rules={requireRules("تصویر")}
            name={"first_item_image"}
            label="تصویر"
            inputProps={{
              placeholder: "لطفا تصویر خود را انتخاب کنید.",
              options: basicData?.data?.data.map((e) => ({
                label: (
                  <img className="w-35 h-35" src={GetFullMediaPath(e.image)} />
                ),
                value: e.number,
              })),
            }}
          />
        </div>
      </FormCard>
      <FormCard
        subTitle="تعریف و تنظیمات پایه مربوط بخش 2 دپارتمان"
        title="اطلاعات اصلی بخش 2 دپارتمان"
      >
        <div className="w-full grid sm:grid-cols-2 grid-cols-1 gap-6">
          <FormItemInput
            name={"second_item_title"}
            rules={requireRules("عنوان")}
            className="col-span-1"
            label="عنوان"
            inputProps={{
              placeholder: "لطفا عنوان خود را وارد کنید.",
            }}
          />
          <FormItemInput
            name={"second_item_sub_title"}
            rules={requireRules("عنوان فرعی")}
            className="col-span-1"
            label="عنوان فرعی"
            inputProps={{
              placeholder: "لطفا عنوان فرعی خود را وارد کنید.",
            }}
          />
          <FormItemInput
            name={"second_item_link"}
            rules={[
              ...requireRules("لینک"),
              {
                type: "url",
                message: "لینک نادرست است",
              },
            ]}
            className="col-span-1"
            label="لینک"
            inputProps={{
              placeholder: "لطفا لینک خود را وارد کنید.",
            }}
          />
          <FormItemInputSelect
            className="col-span-1"
            rules={requireRules("تصویر")}
            name={"second_item_image"}
            label="تصویر"
            inputProps={{
              placeholder: "لطفا تصویر خود را انتخاب کنید.",
              options: basicData?.data?.data.map((e) => ({
                label: (
                  <img className="w-35 h-35" src={GetFullMediaPath(e.image)} />
                ),
                value: e.number,
              })),
            }}
          />
        </div>
      </FormCard>
      <FormCard
        subTitle="تعریف و تنظیمات پایه مربوط بخش 3 دپارتمان"
        title="اطلاعات اصلی بخش 3 دپارتمان"
      >
        <div className="w-full grid sm:grid-cols-2 grid-cols-1 gap-6">
          <FormItemInput
            name={"third_item_title"}
            rules={requireRules("عنوان")}
            className="col-span-1"
            label="عنوان"
            inputProps={{
              placeholder: "لطفا عنوان خود را وارد کنید.",
            }}
          />
          <FormItemInput
            name={"third_item_sub_title"}
            rules={requireRules("عنوان فرعی")}
            className="col-span-1"
            label="عنوان فرعی"
            inputProps={{
              placeholder: "لطفا عنوان فرعی خود را وارد کنید.",
            }}
          />
          <FormItemInput
            name={"third_item_link"}
            rules={[
              ...requireRules("لینک"),
              {
                type: "url",
                message: "لینک نادرست است",
              },
            ]}
            className="col-span-1"
            label="لینک"
            inputProps={{
              placeholder: "لطفا لینک خود را وارد کنید.",
            }}
          />
          <FormItemInputSelect
            className="col-span-1"
            rules={requireRules("تصویر")}
            name={"third_item_image"}
            label="تصویر"
            inputProps={{
              placeholder: "لطفا تصویر خود را انتخاب کنید.",
              options: basicData?.data?.data.map((e) => ({
                label: (
                  <img className="w-35 h-35" src={GetFullMediaPath(e.image)} />
                ),
                value: e.number,
              })),
            }}
          />
        </div>
      </FormCard>
      <FormCard subTitle="توضیح کوتاه و کامل دپارتمان" title="توضیحات دپارتمان">
        <div className="w-full grid sm:grid-cols-2 grid-cols-1 gap-6">
          <div className="col-span-2">
            <Form.Item
              // rules={requireRules("توضیحات دپارتمان")}
              name={"end_page_text"}
              label="توضیحات دپارتمان"
            >
              <div
                dir="auto"
                className="w-full border-r border-l border-b border-grayscale-200 h-96! rounded-br-xl rounded-bl-xl"
                lang="fa"
                ref={editorRef}
              />
            </Form.Item>
          </div>
        </div>
      </FormCard>
      <FormButton
        isPending={addCategory.isPending || update.isPending}
        ActionSaveAndStay={() => {
          form.validateFields().then(() => {
            onFinish(form.getFieldsValue(), false);
          });
        }}
      />
    </Form>
  );
}
